import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ShieldOff } from 'lucide-react';
import Header from '../components/Header';
import { api, formatError } from '../lib/api';
import { toast } from 'sonner';

export default function RemovalRequest() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const tradieId = sp.get('tradie');
  const [trade, setTrade] = useState(null);
  const [reason, setReason] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    document.title = 'Request listing removal — Real Tradies';
    if (tradieId) api.getTradie(tradieId).then((r) => setTrade(r.data)).catch(() => {});
  }, [tradieId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.requestRemoval({ tradie_id: tradieId, reason, contact_email: contactEmail });
      setOk(true);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="removal-request-page">
      <Header />
      <div className="mx-auto max-w-2xl px-6 py-12 md:px-10">
        <Link to={tradieId ? `/tradie/${tradieId}` : '/'} className="inline-flex items-center gap-2 text-sm text-[#5C5B5A] hover:text-[#1A362D]">
          <ArrowLeft size={14} /> Back
        </Link>

        {ok ? (
          <div className="mt-8 border border-[#2A9D8F] bg-[#2A9D8F]/[0.05] p-8 text-center" data-testid="removal-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#2A9D8F] text-white">
              <Check size={30} />
            </div>
            <h2 className="mt-6 font-heading text-2xl font-bold text-[#1C1B1A]">Removal request received.</h2>
            <p className="mt-2 text-sm text-[#5C5B5A]">
              The listing has been hidden from public search immediately. Our team will review your request and delete
              it permanently within one business day. If you'd prefer to claim the listing instead, click Claim on the
              profile page — you'll get full editorial control plus the green Verified tick.
            </p>
            <button onClick={() => nav('/')} className="btn-primary mt-6">Back to home</button>
          </div>
        ) : (
          <div className="mt-8">
            <div className="inline-flex items-center gap-2 border border-[#D96C4E] bg-[#D96C4E]/[0.06] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#D96C4E]">
              <ShieldOff size={12} /> Listing removal
            </div>
            <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-[#1C1B1A]">
              Request removal of a listing.
            </h1>
            <p className="mt-4 text-[#5C5B5A]">
              Real Tradies created this listing from publicly available information ({trade?.business_name ? <><b>{trade.business_name}</b></> : 'this business'}).
              If you'd like it removed, tell us why and we'll take it down within 1 business day. No questions asked — this
              is a legal-compliance mechanism to make sure businesses can always opt out.
            </p>
            <div className="mt-4 rounded border border-dashed border-[#E5E4E0] bg-white p-4 text-sm text-[#5C5B5A]">
              💡 <b>Prefer to claim it?</b> Claiming lets you edit your profile, add photos, hide competitors' rates, and
              get the Verified tick after we check your licence and insurance. Free forever.
              {tradieId && (
                <Link to={`/register?claim=${tradieId}`} className="ml-2 font-semibold text-[#1A362D] underline underline-offset-4">
                  Claim it instead →
                </Link>
              )}
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="removal-form">
              <textarea
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why would you like this listing removed? (Optional but helpful)"
                rows={4}
                className="input-editorial"
                data-testid="removal-reason"
              />
              <input
                type="email"
                required
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="Your email so we can confirm removal"
                className="input-editorial"
                data-testid="removal-email"
              />
              <button type="submit" disabled={busy} className="btn-primary w-full py-3 disabled:opacity-50" data-testid="removal-submit">
                {busy ? 'Sending...' : 'Remove this listing'}
              </button>
              <p className="text-center text-[10px] uppercase tracking-[0.15em] text-[#8A8988]">
                Or email us direct: privacy@realtradies.com.au
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
